import { useRef, useEffect, useCallback, useState } from 'react'
import { Play, Pause, RotateCcw, ZoomIn, ZoomOut, Maximize } from 'lucide-react'
import { useTrajectoryAnimation } from '@/hooks/useTrajectoryAnimation'
import type { PositionPoint } from '@/types/forklift'
import type { BgImageCanvasParams } from '@/types/bgImage'
import type { MapPole, PoleSnapshot } from '@/types/pole'

// プレビューパスの型定義
type PathPoint = [number, number] | { x: number; y: number }

export interface PreviewPath {
  id: string
  slots: string[]
  action: string
  path?: PathPoint[] | null
}

interface TrajectoryCanvasProps {
  positions: PositionPoint[]
  aspectRatio?: number // 高さ = 幅 / aspectRatio (デフォルト: 1.5)
  onCurrentTimeChange?: (time: Date | null) => void
  targetTime?: Date | null // 外部から指定された時刻にシーク
  previewPaths?: PreviewPath[] // シナリオのプレビューパス
  bgImage?: HTMLImageElement | null // 背景画像
  bgImageCanvasParams?: BgImageCanvasParams | null // 背景画像の座標パラメータ
  mapPoles?: MapPole[] | null // ポールマップ（既知ポール位置）
  poleSnapshots?: PoleSnapshot[] // 検出ポールのスナップショット（時刻昇順）
}

// パスポイントからx, y座標を取得するヘルパー関数
function getPathCoords(point: PathPoint): { x: number; y: number } {
  if (Array.isArray(point)) {
    return { x: point[0], y: point[1] }
  }
  return { x: point.x, y: point.y }
}

interface Bounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

const SPEED_OPTIONS = [1, 4, 8, 16]
// パディング設定（グラフ領域の周囲の余白）
const PADDING_LEFT = 45
const PADDING_RIGHT = 30
const PADDING_TOP = 30
const PADDING_BOTTOM = 30

const MIN_RANGE = 4 // 最小表示範囲（メートル）

// ポール種別ごとの表示色（fork_webapp_react の CATEGORIZED_POLE_COLORS と同一）
const CATEGORIZED_POLE_COLORS: Record<string, string> = {
  detected_pole_matched: 'rgba(0, 200, 255, 0.7)', // シアン: マップに正しくマッチ
  detected_pole_unmatched: 'rgba(30, 58, 138, 0.7)', // 暗青: マップから外れた検出（自己位置に未使用）
  detected_pole_excluded_ransac: 'rgba(249, 115, 22, 0.5)', // 橙: マップからズレと判定され除外
  detected_pole_excluded_multiple_match: 'rgba(249, 115, 22, 0.5)', // 橙: 1本に複数マッチで除外
  detected_pole_excluded_masked_area: 'rgba(200, 200, 200, 0.5)', // 灰: マスクエリア内のため除外
}
const CATEGORIZED_POLE_FALLBACK_COLOR = 'rgba(200, 200, 200, 0.5)' // 未知の ns 用

// 再生時刻からこれより古い検出ポールスナップショットは表示しない
const POLE_SNAPSHOT_MAX_AGE_MS = 60 * 1000

// 自己位置マーカーの最小サイズ（ズームアウト時も視認できる下限）。
// 広域表示で軌跡を隠さないよう fork_webapp_react の 40px より小さくしている
const MIN_ROBOT_ICON_SIZE_PX = 10

// フォークリフト型マーカーを描画
// fork_webapp_react の drawForklift (hooks/useMapCanvas.ts) から移植
function drawForklift(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  theta: number,
  color: string,
  size: number,
  bodyColor = '#4b5563'
) {
  // 寸法定義（size基準）
  const bodyW = size * 1.2
  const bodyH = size * 1.1
  const forkLen = size * 1.1
  const forkW = size * 0.25
  const forkGap = size * 0.55 // フォーク爪の中心間距離
  const cwW = size * 1.2 // 後部ストリップ幅
  const cwH = size * 0.4 // 後部ストリップ高さ
  const r = size * 0.1 // 角丸半径
  // 爪の付け根オフセット（移植元の固定 15px を size=50 基準で正規化）
  const forkRootOffset = size * 0.3

  ctx.save()
  ctx.translate(x, y)
  // Canvas座標系: Y軸下向き。theta=0で右向き（ROS座標系）
  // -theta でROS座標→Canvas座標に変換し、-PI/2 で「上向き=前方」に補正
  ctx.rotate(-theta - Math.PI / 2)
  // 描画中心をオフセット
  ctx.translate(0, size * 0.5)

  // フォーク爪（前方・2本の角丸長方形）
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(
    -forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + forkRootOffset,
    forkW,
    forkLen,
    r
  )
  ctx.fill()
  ctx.beginPath()
  ctx.roundRect(
    forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + forkRootOffset,
    forkW,
    forkLen,
    r
  )
  ctx.fill()

  // 本体
  ctx.fillStyle = bodyColor
  ctx.beginPath()
  ctx.roundRect(-bodyW / 2, -bodyH / 2, bodyW, bodyH, r)
  ctx.fill()

  // 後部ストリップ
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(-cwW / 2, bodyH / 2 - cwH * 0.8, cwW, cwH, r)
  ctx.fill()

  ctx.restore()
}

// 背景画像がカバーするROS座標範囲を計算
function getBgImageRosBounds(
  bgImage: HTMLImageElement,
  params: BgImageCanvasParams
) {
  const ppm = params.imagePixelPerMeter
  return {
    minX: -params.imageOriginPixX / ppm,
    maxX: (bgImage.width - params.imageOriginPixX) / ppm,
    minY: -(bgImage.height - params.imageOriginPixY) / ppm,
    maxY: params.imageOriginPixY / ppm,
  }
}

function calculateBounds(
  positions: PositionPoint[],
  canvasWidth: number,
  canvasHeight: number,
  previewPaths?: PreviewPath[],
  bgImage?: HTMLImageElement | null,
  bgImageCanvasParams?: BgImageCanvasParams | null
): Bounds | null {
  // 位置データもプレビューパスもない場合はnull
  const hasPositions = positions.length > 0
  const hasPreviewPaths =
    previewPaths && previewPaths.some((p) => p.path && p.path.length > 0)
  if (!hasPositions && !hasPreviewPaths) return null

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  // 位置データから境界を計算
  for (const p of positions) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }

  // プレビューパスから境界を計算
  if (previewPaths) {
    for (const previewPath of previewPaths) {
      if (!previewPath.path) continue
      for (const point of previewPath.path) {
        const { x, y } = getPathCoords(point)
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  // 背景画像の範囲を含める
  if (bgImage && bgImageCanvasParams) {
    const imgBounds = getBgImageRosBounds(bgImage, bgImageCanvasParams)
    if (imgBounds.minX < minX) minX = imgBounds.minX
    if (imgBounds.maxX > maxX) maxX = imgBounds.maxX
    if (imgBounds.minY < minY) minY = imgBounds.minY
    if (imgBounds.maxY > maxY) maxY = imgBounds.maxY
  }

  // 最小範囲を確保（4m x 4m）
  let xRange = maxX - minX
  let yRange = maxY - minY

  if (xRange < MIN_RANGE) {
    const centerX = (minX + maxX) / 2
    minX = centerX - MIN_RANGE / 2
    maxX = centerX + MIN_RANGE / 2
    xRange = MIN_RANGE
  }

  if (yRange < MIN_RANGE) {
    const centerY = (minY + maxY) / 2
    minY = centerY - MIN_RANGE / 2
    maxY = centerY + MIN_RANGE / 2
    yRange = MIN_RANGE
  }

  // キャンバスのアスペクト比に合わせてboundsを拡張
  // これによりグリッドがキャンバス全体を埋める
  const drawWidth = canvasWidth - PADDING_LEFT - PADDING_RIGHT
  const drawHeight = canvasHeight - PADDING_TOP - PADDING_BOTTOM
  const canvasAspect = drawWidth / drawHeight
  const dataAspect = xRange / yRange

  if (dataAspect < canvasAspect) {
    // データが縦長の場合、X方向を拡張
    const targetXRange = yRange * canvasAspect
    const centerX = (minX + maxX) / 2
    minX = centerX - targetXRange / 2
    maxX = centerX + targetXRange / 2
  } else if (dataAspect > canvasAspect) {
    // データが横長の場合、Y方向を拡張
    const targetYRange = xRange / canvasAspect
    const centerY = (minY + maxY) / 2
    minY = centerY - targetYRange / 2
    maxY = centerY + targetYRange / 2
  }

  return { minX, maxX, minY, maxY }
}

export default function TrajectoryCanvas({
  positions,
  aspectRatio = 1.8, // 横長にして高さを抑える
  onCurrentTimeChange,
  targetTime,
  bgImage,
  bgImageCanvasParams,
  previewPaths,
  mapPoles,
  poleSnapshots,
}: TrajectoryCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 400 })

  // ポール表示のON/OFF
  const [showMapPoles, setShowMapPoles] = useState(true)
  const [showDetectedPoles, setShowDetectedPoles] = useState(true)

  // パン/ズーム状態
  const [zoomScale, setZoomScale] = useState(1)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 }) // ROS座標系でのオフセット
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef({
    clientX: 0,
    clientY: 0,
    offsetX: 0,
    offsetY: 0,
  })
  const touchStartRef = useRef({ distance: 0, scale: 1 })

  // コンテナサイズに合わせてキャンバスサイズを更新
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateSize = () => {
      const containerWidth = container.clientWidth
      if (containerWidth > 0) {
        const newWidth = containerWidth
        const newHeight = Math.round(containerWidth / aspectRatio)
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    // 初期サイズ設定
    updateSize()

    // ResizeObserverでコンテナサイズ変更を監視
    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [aspectRatio])

  const { width, height } = canvasSize

  const {
    state: animState,
    currentPosition,
    visiblePath,
    play,
    pause,
    reset,
    setSpeed,
    seekToProgress,
    seekToTime,
  } = useTrajectoryAnimation({
    positions,
  })

  // positionsの変更を検出するためのキー
  const positionsKey =
    positions.length > 0 ? `${positions.length}-${positions[0]?.timestamp}` : ''

  // 外部から指定された時刻にシーク
  // positions更新後にも確実にシークを実行するため、positionsKeyも依存に含める
  useEffect(() => {
    if (targetTime && positions.length > 0) {
      // 少し遅延を入れて、reset effectが完了した後にシークを実行
      const timer = setTimeout(() => {
        seekToTime(targetTime)
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [targetTime, positionsKey, seekToTime, positions.length])

  // 現在の再生時刻を親に通知
  useEffect(() => {
    if (onCurrentTimeChange) {
      if (currentPosition) {
        onCurrentTimeChange(new Date(currentPosition.timestamp))
      } else {
        onCurrentTimeChange(null)
      }
    }
  }, [currentPosition, onCurrentTimeChange])

  // ズーム/パンをboundsに適用
  const applyZoomPan = useCallback(
    (bounds: Bounds): Bounds => {
      const rangeX = bounds.maxX - bounds.minX
      const rangeY = bounds.maxY - bounds.minY
      // panOffsetは0〜1の比率なのでboundsの範囲に変換
      const centerX = (bounds.minX + bounds.maxX) / 2 - panOffset.x * rangeX
      const centerY = (bounds.minY + bounds.maxY) / 2 - panOffset.y * rangeY
      const halfW = rangeX / 2 / zoomScale
      const halfH = rangeY / 2 / zoomScale
      return {
        minX: centerX - halfW,
        maxX: centerX + halfW,
        minY: centerY - halfH,
        maxY: centerY + halfH,
      }
    },
    [zoomScale, panOffset]
  )

  // 座標変換関数（ROSのx,y座標をCanvas座標に変換）
  const createTransform = useCallback(
    (bounds: Bounds) => {
      const drawWidth = width - PADDING_LEFT - PADDING_RIGHT
      const drawHeight = height - PADDING_TOP - PADDING_BOTTOM

      const xRange = bounds.maxX - bounds.minX
      const yRange = bounds.maxY - bounds.minY

      // アスペクト比を維持
      const scaleX = drawWidth / xRange
      const scaleY = drawHeight / yRange
      const scale = Math.min(scaleX, scaleY)

      const offsetX = PADDING_LEFT + (drawWidth - xRange * scale) / 2
      const offsetY = PADDING_TOP + (drawHeight - yRange * scale) / 2

      // ROSの座標系: x=前方, y=左方向
      // Canvas: x=右方向, y=下方向
      // ROSのy軸を反転してCanvas表示
      return (x: number, y: number) => ({
        x: offsetX + (x - bounds.minX) * scale,
        y: offsetY + (bounds.maxY - y) * scale, // Y軸は反転
      })
    },
    [width, height]
  )

  // Canvas描画
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // 位置データもプレビューパスもない場合は描画しない
    const hasPreviewPaths =
      previewPaths && previewPaths.some((p) => p.path && p.path.length > 0)
    if (positions.length === 0 && !hasPreviewPaths) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const baseBounds = calculateBounds(
      positions,
      width,
      height,
      previewPaths,
      bgImage,
      bgImageCanvasParams
    )
    if (!baseBounds) return

    const bounds = applyZoomPan(baseBounds)
    const transform = createTransform(bounds)

    // クリア
    ctx.clearRect(0, 0, width, height)

    // 背景
    ctx.fillStyle = '#f8fafc'
    ctx.fillRect(0, 0, width, height)

    // 背景画像を描画
    if (bgImage && bgImageCanvasParams) {
      const imgRos = getBgImageRosBounds(bgImage, bgImageCanvasParams)
      const topLeft = transform(imgRos.minX, imgRos.maxY)
      const bottomRight = transform(imgRos.maxX, imgRos.minY)
      ctx.globalAlpha = 0.7
      ctx.drawImage(
        bgImage,
        topLeft.x,
        topLeft.y,
        bottomRight.x - topLeft.x,
        bottomRight.y - topLeft.y
      )
      ctx.globalAlpha = 1.0
    }

    // スケール計算
    const drawWidth = width - PADDING_LEFT - PADDING_RIGHT
    const drawHeight = height - PADDING_TOP - PADDING_BOTTOM
    const xRange = bounds.maxX - bounds.minX
    const yRange = bounds.maxY - bounds.minY
    const scaleX = drawWidth / xRange
    const scaleY = drawHeight / yRange
    const scale = Math.min(scaleX, scaleY)

    // 実際に描画される範囲（アスペクト比維持後）
    const actualDrawWidth = xRange * scale
    const actualDrawHeight = yRange * scale
    const drawOffsetX = PADDING_LEFT + (drawWidth - actualDrawWidth) / 2
    const drawOffsetY = PADDING_TOP + (drawHeight - actualDrawHeight) / 2

    // グリッド間隔を計算（1m, 2m, 5m, 10m, 20m, 50m... から適切な間隔を選択）
    const gridSteps = [0.5, 1, 2, 5, 10, 20, 50, 100]
    const targetGridCount = 5 // 目標グリッド数
    const maxRange = Math.max(xRange, yRange)
    let gridStep =
      gridSteps.find((step) => maxRange / step <= targetGridCount * 2) || 100

    // グリッド線
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    ctx.fillStyle = '#94a3b8'
    ctx.font = '10px sans-serif'

    // X軸のグリッド線と目盛り
    const xStart = Math.ceil(bounds.minX / gridStep) * gridStep
    for (let x = xStart; x <= bounds.maxX; x += gridStep) {
      const canvasX = drawOffsetX + (x - bounds.minX) * scale
      ctx.beginPath()
      ctx.moveTo(canvasX, drawOffsetY)
      ctx.lineTo(canvasX, drawOffsetY + actualDrawHeight)
      ctx.stroke()
      // 目盛りラベル
      ctx.textAlign = 'center'
      ctx.fillText(x.toFixed(1), canvasX, drawOffsetY + actualDrawHeight + 12)
    }

    // Y軸のグリッド線と目盛り
    const yStart = Math.ceil(bounds.minY / gridStep) * gridStep
    for (let y = yStart; y <= bounds.maxY; y += gridStep) {
      const canvasY = drawOffsetY + (bounds.maxY - y) * scale // Y軸反転
      ctx.beginPath()
      ctx.moveTo(drawOffsetX, canvasY)
      ctx.lineTo(drawOffsetX + actualDrawWidth, canvasY)
      ctx.stroke()
      // 目盛りラベル
      ctx.textAlign = 'right'
      ctx.fillText(y.toFixed(1), drawOffsetX - 4, canvasY + 3)
    }

    // ポールマップ（既知ポール）: 灰色の円で描画
    // fork_webapp_react の drawMapPoles (common/drawUtil.ts) から移植
    if (showMapPoles && mapPoles && mapPoles.length > 0) {
      const poleRadius = scale * 0.28
      ctx.fillStyle = 'rgba(128, 128, 128, 0.5)'
      for (const pole of mapPoles) {
        const pos = transform(pole.x, pole.y)
        ctx.beginPath()
        ctx.arc(pos.x, pos.y, poleRadius, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // 検出ポール: 再生時刻直前のスナップショットを種別(ns)ごとに色分けして描画
    // fork_webapp_react の drawCategorizedPoles (common/drawUtil.ts) から移植
    if (
      showDetectedPoles &&
      poleSnapshots &&
      poleSnapshots.length > 0 &&
      currentPosition
    ) {
      const currentMs = new Date(currentPosition.timestamp).getTime()
      let snapshot: PoleSnapshot | null = null
      for (const s of poleSnapshots) {
        if (new Date(s.timestamp).getTime() > currentMs) break
        snapshot = s
      }
      if (
        snapshot &&
        currentMs - new Date(snapshot.timestamp).getTime() <=
          POLE_SNAPSHOT_MAX_AGE_MS
      ) {
        const poleRadius = scale * 0.15
        for (const pole of snapshot.poles) {
          const pos = transform(pole.x, pole.y)
          const color =
            CATEGORIZED_POLE_COLORS[pole.ns] ?? CATEGORIZED_POLE_FALLBACK_COLOR

          // 外側の円（白い縁取り）で背景に対する視認性を確保
          ctx.strokeStyle = 'white'
          ctx.lineWidth = 3
          ctx.beginPath()
          ctx.arc(pos.x, pos.y, poleRadius + 1, 0, Math.PI * 2)
          ctx.stroke()

          // 種別色で塗りつぶし
          ctx.fillStyle = color
          ctx.beginPath()
          ctx.arc(pos.x, pos.y, poleRadius, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    // プレビューパス（シナリオの予定経路）- 紫で表示
    if (previewPaths && previewPaths.length > 0) {
      const previewColor = '#a78bfa' // violet-400
      previewPaths.forEach((previewPath) => {
        if (!previewPath.path || previewPath.path.length < 2) return

        ctx.strokeStyle = previewColor
        ctx.lineWidth = 3
        ctx.setLineDash([8, 4])
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()

        previewPath.path.forEach((point, i) => {
          const { x, y } = getPathCoords(point)
          const pos = transform(x, y)
          if (i === 0) {
            ctx.moveTo(pos.x, pos.y)
          } else {
            ctx.lineTo(pos.x, pos.y)
          }
        })
        ctx.stroke()
        ctx.setLineDash([])

        // プレビューパスの開始点（紫の三角マーカー）
        const startPoint = previewPath.path[0]
        const { x: sx, y: sy } = getPathCoords(startPoint)
        const startPos = transform(sx, sy)
        ctx.fillStyle = previewColor
        ctx.beginPath()
        ctx.moveTo(startPos.x, startPos.y - 8)
        ctx.lineTo(startPos.x - 6, startPos.y + 4)
        ctx.lineTo(startPos.x + 6, startPos.y + 4)
        ctx.closePath()
        ctx.fill()

        // プレビューパスの終了点（紫の四角マーカー）
        const endPoint = previewPath.path[previewPath.path.length - 1]
        const { x: ex, y: ey } = getPathCoords(endPoint)
        const endPos = transform(ex, ey)
        ctx.fillStyle = previewColor
        ctx.fillRect(endPos.x - 5, endPos.y - 5, 10, 10)
      })
    }

    // 全体経路（薄い点線、自動/手動モードで色分け）
    // 自動モード: 青系(薄), 手動モード: オレンジ系(薄)
    const getTrailColor = (isAutoMode: boolean | null) =>
      isAutoMode === false ? '#9ca3af' : '#93c5fd' // 手動=gray-400, 自動/不明=blue-300
    const getTrailLineWidth = (isAutoMode: boolean | null) =>
      isAutoMode === false ? 1 : 2 // 手動=細め, 自動=通常
    ctx.setLineDash([5, 5])
    if (positions.length > 0) {
      let prevMode = positions[0].isAutoMode
      ctx.strokeStyle = getTrailColor(prevMode)
      ctx.lineWidth = getTrailLineWidth(prevMode)
      ctx.beginPath()
      positions.forEach((p, i) => {
        const pos = transform(p.x, p.y)
        const mode = p.isAutoMode
        if (i === 0) {
          ctx.moveTo(pos.x, pos.y)
        } else if (mode !== prevMode) {
          ctx.lineTo(pos.x, pos.y)
          ctx.stroke()
          ctx.strokeStyle = getTrailColor(mode)
          ctx.lineWidth = getTrailLineWidth(mode)
          ctx.beginPath()
          ctx.moveTo(pos.x, pos.y)
          prevMode = mode
        } else {
          ctx.lineTo(pos.x, pos.y)
        }
      })
      ctx.stroke()
    }
    ctx.setLineDash([])

    // 進行済み経路（実線、自動/手動モードで色分け: 自動=青, 手動=オレンジ）
    const getPathColor = (isAutoMode: boolean | null) =>
      isAutoMode === false ? '#9ca3af' : '#3b82f6' // 手動=gray-400, 自動/不明=blue-500
    const getPathLineWidth = (isAutoMode: boolean | null) =>
      isAutoMode === false ? 1.5 : 3 // 手動=細め, 自動=太め
    if (visiblePath.length > 1) {
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      let prevMode = visiblePath[0].isAutoMode
      ctx.strokeStyle = getPathColor(prevMode)
      ctx.lineWidth = getPathLineWidth(prevMode)
      ctx.beginPath()
      visiblePath.forEach((p, i) => {
        const pos = transform(p.x, p.y)
        const mode = p.isAutoMode
        if (i === 0) {
          ctx.moveTo(pos.x, pos.y)
        } else if (mode !== prevMode) {
          ctx.lineTo(pos.x, pos.y)
          ctx.stroke()
          ctx.strokeStyle = getPathColor(mode)
          ctx.lineWidth = getPathLineWidth(mode)
          ctx.beginPath()
          ctx.moveTo(pos.x, pos.y)
          prevMode = mode
        } else {
          ctx.lineTo(pos.x, pos.y)
        }
      })
      ctx.stroke()
    }

    // 開始点（緑の円）- 位置データがある場合のみ
    if (positions.length > 0) {
      const startPos = transform(positions[0].x, positions[0].y)
      ctx.fillStyle = '#22c55e'
      ctx.beginPath()
      ctx.arc(startPos.x, startPos.y, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 10px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('S', startPos.x, startPos.y)

      // 終了点（赤の円）- アニメーション完了時のみ表示
      if (!animState.isPlaying && animState.progress === 100) {
        const endP = positions[positions.length - 1]
        const endPos = transform(endP.x, endP.y)
        ctx.fillStyle = '#ef4444'
        ctx.beginPath()
        ctx.arc(endPos.x, endPos.y, 8, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.fillText('E', endPos.x, endPos.y)
      }
    }

    // 現在位置（フォーク型マーカー、fork_webapp_react と同じ形状・縮尺）
    // インデックス0でも表示（シーク時の視覚的フィードバック）
    if (currentPosition) {
      const curPos = transform(currentPosition.x, currentPosition.y)
      // マーカーサイズ = 1m相当のピクセル数。ズームアウト時も視認できるよう下限あり
      const robotShapeSize = Math.max(scale, MIN_ROBOT_ICON_SIZE_PX)
      drawForklift(
        ctx,
        curPos.x,
        curPos.y,
        currentPosition.theta ?? 0,
        '#60a5fa',
        robotShapeSize
      )
    }
  }, [
    positions,
    visiblePath,
    currentPosition,
    animState,
    width,
    height,
    createTransform,
    previewPaths,
    bgImage,
    bgImageCanvasParams,
    mapPoles,
    poleSnapshots,
    showMapPoles,
    showDetectedPoles,
    applyZoomPan,
  ])

  // パン/ズーム イベントハンドラ
  const MIN_ZOOM = 0.5
  const MAX_ZOOM = 20

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault()
    const factor = e.deltaY > 0 ? 0.9 : 1.1
    setZoomScale((s) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, s * factor)))
  }, [])

  const handleMouseDown = useCallback((e: MouseEvent) => {
    isDraggingRef.current = true
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      offsetX: 0,
      offsetY: 0,
    }
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return
      const canvas = canvasRef.current
      if (!canvas) return

      const dx = e.clientX - dragStartRef.current.clientX
      const dy = e.clientY - dragStartRef.current.clientY

      // baseBoundsの範囲をキャンバスサイズで割ってピクセル→ROS変換比率を得る
      const drawW = width - PADDING_LEFT - PADDING_RIGHT
      const drawH = height - PADDING_TOP - PADDING_BOTTOM

      // baseBoundsが必要だが再計算は重いのでキャンバスのピクセル比で近似
      // ズーム中のROS座標1ピクセル分の移動量は、boundsの範囲 / drawPixels
      // 簡易的にキャンバスの描画範囲あたりの移動量で計算
      setPanOffset((prev) => ({
        x: prev.x + dx / drawW / zoomScale,
        y: prev.y - dy / drawH / zoomScale, // Y軸反転
      }))

      dragStartRef.current.clientX = e.clientX
      dragStartRef.current.clientY = e.clientY
    },
    [width, height, zoomScale]
  )

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // タッチイベント（ピンチズーム + ドラッグ）
  const getTouchDistance = (t1: Touch, t2: Touch) =>
    Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY)

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        touchStartRef.current = {
          distance: getTouchDistance(e.touches[0], e.touches[1]),
          scale: zoomScale,
        }
      } else if (e.touches.length === 1) {
        isDraggingRef.current = true
        dragStartRef.current = {
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          offsetX: 0,
          offsetY: 0,
        }
      }
    },
    [zoomScale]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const newDist = getTouchDistance(e.touches[0], e.touches[1])
        const scaleChange = newDist / touchStartRef.current.distance
        const newScale = touchStartRef.current.scale * scaleChange
        setZoomScale(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newScale)))
      } else if (e.touches.length === 1 && isDraggingRef.current) {
        const dx = e.touches[0].clientX - dragStartRef.current.clientX
        const dy = e.touches[0].clientY - dragStartRef.current.clientY
        const drawW = width - PADDING_LEFT - PADDING_RIGHT
        const drawH = height - PADDING_TOP - PADDING_BOTTOM
        setPanOffset((prev) => ({
          x: prev.x + dx / drawW / zoomScale,
          y: prev.y - dy / drawH / zoomScale,
        }))
        dragStartRef.current.clientX = e.touches[0].clientX
        dragStartRef.current.clientY = e.touches[0].clientY
      }
    },
    [width, height, zoomScale]
  )

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // イベントリスナーの登録
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.addEventListener('wheel', handleWheel, { passive: false })
    canvas.addEventListener('mousedown', handleMouseDown)
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      canvas.removeEventListener('wheel', handleWheel)
      canvas.removeEventListener('mousedown', handleMouseDown)
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ])

  const handleZoomIn = () => setZoomScale((s) => Math.min(MAX_ZOOM, s * 1.3))
  const handleZoomOut = () => setZoomScale((s) => Math.max(MIN_ZOOM, s / 1.3))
  const handleResetView = () => {
    setZoomScale(1)
    setPanOffset({ x: 0, y: 0 })
  }

  const handlePlayPause = () => {
    if (animState.isPlaying) {
      pause()
    } else {
      play()
    }
  }

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    seekToProgress(parseFloat(e.target.value))
  }

  // プレビューパスがあるかチェック
  const hasPreviewPathsForRender =
    previewPaths && previewPaths.some((p) => p.path && p.path.length > 0)

  if (positions.length === 0 && !hasPreviewPathsForRender) {
    return (
      <div ref={containerRef} className="w-full">
        <div
          className="flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
          style={{ height: Math.round(width / aspectRatio) }}
        >
          <span className="text-gray-500">位置データがありません</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 w-full">
      {/* Canvas */}
      <div
        ref={containerRef}
        className="border border-gray-200 rounded-lg overflow-hidden relative"
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="cursor-grab active:cursor-grabbing"
        />
        {/* ズームコントロール */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <button
            onClick={handleZoomIn}
            className="w-8 h-8 bg-white/90 border border-gray-300 rounded shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
            title="ズームイン"
          >
            <ZoomIn className="w-4 h-4 text-gray-700" />
          </button>
          <button
            onClick={handleZoomOut}
            className="w-8 h-8 bg-white/90 border border-gray-300 rounded shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
            title="ズームアウト"
          >
            <ZoomOut className="w-4 h-4 text-gray-700" />
          </button>
          <button
            onClick={handleResetView}
            className="w-8 h-8 bg-white/90 border border-gray-300 rounded shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
            title="リセット"
          >
            <Maximize className="w-4 h-4 text-gray-700" />
          </button>
        </div>
        {/* ポール表示切り替え */}
        {((mapPoles && mapPoles.length > 0) ||
          (poleSnapshots && poleSnapshots.length > 0)) && (
          <div className="absolute top-2 left-2 flex flex-col gap-1 bg-white/90 border border-gray-300 rounded shadow-sm px-2 py-1.5 text-xs text-gray-700">
            {mapPoles && mapPoles.length > 0 && (
              <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showMapPoles}
                  onChange={(e) => setShowMapPoles(e.target.checked)}
                  className="accent-primary-600"
                />
                ポールマップ
              </label>
            )}
            {poleSnapshots && poleSnapshots.length > 0 && (
              <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showDetectedPoles}
                  onChange={(e) => setShowDetectedPoles(e.target.checked)}
                  className="accent-primary-600"
                />
                検出ポール
              </label>
            )}
          </div>
        )}
        {zoomScale !== 1 && (
          <span className="absolute bottom-2 right-2 text-xs text-gray-500 bg-white/80 px-1.5 py-0.5 rounded">
            {zoomScale.toFixed(1)}x
          </span>
        )}
      </div>

      {/* コントロール */}
      <div className="space-y-3">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
          {/* 再生/一時停止 */}
          <button
            onClick={handlePlayPause}
            className="hidden md:flex items-center justify-center w-10 h-10 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
          >
            {animState.isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </button>

          {/* リセット */}
          <button
            onClick={reset}
            className="hidden md:flex items-center justify-center w-10 h-10 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          {/* 速度選択 */}
          <div className="hidden md:flex items-center gap-2">
            <span className="text-sm text-gray-600">速度:</span>
            <div className="flex gap-1">
              {SPEED_OPTIONS.map((speed) => (
                <button
                  key={speed}
                  onClick={() => setSpeed(speed)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    animState.speed === speed
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>

          {/* 現在時刻 */}
          {animState.currentTime > 0 && (
            <span className="ml-auto text-sm font-medium text-primary-600 whitespace-nowrap">
              {new Date(animState.currentTime).toLocaleString('ja-JP')}
            </span>
          )}
        </div>

        {/* 位置・速度・フォーク高 */}
        {currentPosition && (
          <div className="text-sm text-gray-600 flex flex-row flex-wrap gap-x-4 gap-y-1">
            <div>
              <span className="whitespace-nowrap">[位置]</span>{' '}
              <span className="whitespace-nowrap">
                ({currentPosition.x.toFixed(2)}, {currentPosition.y.toFixed(2)})
              </span>
            </div>
            <div>
              <span className="whitespace-nowrap">[速度]</span>{' '}
              <span className="whitespace-nowrap">
                {currentPosition.speed.toFixed(1)} m/s (
                {(currentPosition.speed * 3.6).toFixed(1)} km/h)
              </span>
            </div>
            {currentPosition.forkHeight !== null && (
              <div>
                <span className="whitespace-nowrap">[フォーク高]</span>{' '}
                <span className="whitespace-nowrap">
                  {(currentPosition.forkHeight * 1000).toFixed(0)}mm
                </span>
              </div>
            )}
            <div>
              <span className="whitespace-nowrap">[モード]</span>{' '}
              <span
                className={`whitespace-nowrap ${
                  currentPosition.isAutoMode === false
                    ? 'text-gray-600 font-medium'
                    : 'text-blue-500 font-medium'
                }`}
              >
                {currentPosition.isAutoMode === false
                  ? '手動'
                  : currentPosition.isAutoMode === true
                    ? '自動'
                    : '不明'}
              </span>
            </div>
            <div>
              <span className="whitespace-nowrap">[在荷]</span>{' '}
              {(() => {
                const l = currentPosition.loadSwitchLeft
                const r = currentPosition.loadSwitchRight
                if (l == null && r == null) {
                  return (
                    <span className="whitespace-nowrap text-gray-400 font-medium">
                      不明
                    </span>
                  )
                }
                if (l && r) {
                  return (
                    <span className="whitespace-nowrap text-green-600 font-medium">
                      両ON
                    </span>
                  )
                }
                if (l) {
                  return (
                    <span className="whitespace-nowrap text-amber-600 font-medium">
                      左ON
                    </span>
                  )
                }
                if (r) {
                  return (
                    <span className="whitespace-nowrap text-amber-600 font-medium">
                      右ON
                    </span>
                  )
                }
                return (
                  <span className="whitespace-nowrap text-gray-600 font-medium">
                    OFF
                  </span>
                )
              })()}
            </div>
          </div>
        )}

        {/* プログレスバー */}
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={animState.progress}
            onChange={handleProgressChange}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
          />
          <span className="hidden md:inline text-xs text-gray-500 w-24 whitespace-nowrap text-right">
            {animState.currentIndex + 1} / {positions.length}
          </span>
        </div>
      </div>
    </div>
  )
}
